<template>
	<button
		class="c-Checkbox"
		:class="checkboxClassObj"
		@mousedown.prevent
		@focus="toggleFocus(true)"
		@blur="toggleFocus(false)"
		@keyup.enter="handleKeyup"
		@keyup.space="handleKeyup">
		<input
			:id="checkboxId"
			ref="checkboxElem"
			v-model="value"
			type="checkbox"
			class="c-Checkbox__input"
			:disabled="disabled"
			@change="selectOption">
		<label
			class="c-Checkbox__label"
			:for="checkboxId">
			<span>{{ label }}</span>
		</label>
	</button>
</template>

<script>
import { randomizeId } from '../../utils/utils';

export default {
	name: 'Checkbox',
	props: {
		/**
     * The Checkbox label
     */
		label: {
			type: String,
			required: true,
			default: ''
		},
		/**
     * The Checkbox data variable
     */
		data: {
			type: [Boolean, String, Number],
			required: false,
			default: false
		},
		/**
     * Override the Checkbox default boolean data model (you can set and get the component value according to this model)
		 * @values [value for checked, value for unchecked]
     */
		dataModel: {
			type: Array,
			required: false,
			default: () => ([true, false])
		},
		/**
     * If the Checkbox is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: false
		},
		/**
     * If the Button is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data () {
		return {
			checkboxId: randomizeId('checkboxid'),
			value: false,
			isFocused: false
		}
	},
	computed: {
		checkboxClassObj () {
			return {
				focus: this.isFocused,
				isActive: this.isActive,
				disabled: this.disabled
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				const [checked, unchecked] = this.dataModel

				if (newVal === checked) {
					this.value = true
				} else if (newVal === unchecked) {
					this.value = false
				} else {
					// eslint-disable-next-line no-console
					console.error(`Invalid data value for Checkbox - Does not correlate with the Checkbox dataModel - ${this.dataModel}`)
				}
			},
			immediate: true
		}
	},
	methods: {
		toggleFocus (arg) {
			this.isFocused = arg
		},
		handleKeyup () {
			this.$refs.checkboxElem.click()
		},
		selectOption () {
			/**
       * Triggers when Checkbox changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
			const [checked, unchecked] = this.dataModel

			if (this.value) {
				this.$emit('change', checked)
			} else {
				this.$emit('change', unchecked)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-Checkbox {
  $label-font-size: 16px;

  height: 50px;
  border: none;
  outline: none;
  background-color: transparent;
  width: 100%;
  margin: 0;
  padding: 0;

  &::-moz-focus-inner {
    border: 0;
  }

  &.disabled {
    .c-Checkbox__label {
      color: $un-n500 !important;

      span {
        cursor: not-allowed;

        &::before {
          border-color: $un-n500 !important;
          box-shadow: none !important;
          background-color: $un-n100 !important;
        }
      }
    }

    .c-Checkbox__input:checked + .c-Checkbox__label span::before {
      background-color: $un-n500 !important;
    }
  }

  &.focus .c-Checkbox__label,
  &.isActive .c-Checkbox__label {
    color: $un-blue;

    span::before {
      border-color: $un-blue;
      box-shadow: inset 0 0 0 1px $un-blue;
    }
  }

  &__input {
    width: 0;
    height: 0;
    visibility: hidden;
    opacity: 0;
    position: absolute;

    &:checked + .c-Checkbox__label span::before {
      background-color: $un-blue;
      background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3C!-- Generator: Sketch 61.2 (89653) - https://sketch.com --%3E%3Ctitle%3ECheckbox/selected%3C/title%3E%3Cdesc%3ECreated with Sketch.%3C/desc%3E%3Cg id='Checkbox/selected' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' opacity='1'%3E%3Cpath d='M14.6986428,6.27716754 C14.7038152,6.28222495 14.7089305,6.28734032 14.713988,6.29251267 L14.7209017,6.29958353 C15.1008922,6.6882107 15.1008922,7.30919077 14.7209017,7.69781794 L8.84478758,13.7074873 C8.47783666,14.0827785 7.87985994,14.0953942 7.49749137,13.7403157 C7.45564786,13.710907 7.41534638,13.6775103 7.37733117,13.6403399 L7.36198604,13.6249948 L7.36198604,13.6249948 L5.27909832,11.4947661 C4.89910778,11.106139 4.89910778,10.4851589 5.27909832,10.0965317 L5.28601205,10.0894609 C5.65888656,9.70811148 6.27030585,9.70124123 6.65165524,10.0741157 C6.6568276,10.0791732 6.66194296,10.0842885 6.66700037,10.0894609 L8.143,11.599 L13.3329996,6.29251267 C13.7058741,5.91116328 14.3172934,5.90429303 14.6986428,6.27716754 Z' id='Combined-Shape' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
    }
  }

  &__label {
    font-size: $label-font-size;
    color: $un-n700;
    text-align: left;
    width: 100%;
    height: 100%;

    span {
      cursor: pointer;
      padding: calc(#{$base-padding / 2}) 0;
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      outline: none;

      &::-moz-focus-inner {
        border: 0;
      }

      &::before {
        content: '';
        position: relative;
        width: 20px;
        height: 20px;
        border: 1px solid $un-n700;
        border-radius: $border-radius;
        background-color: #FFFFFF;
        display: block;
        margin-right: calc(#{$base-margin} / 2);
        transition: all $default-time $default-easing;
      }
    }
  }
}
</style>
